/**
 * MODIFIED: 2025-01-29 - refactor/task-1.5-dashboard-performance
 *
 * ARCHITECTURE: Service Architecture
 * PURPOSE: Dependency graph with topological sort for intelligent service initialization ordering
 *
 * NEW PATTERNS IMPLEMENTED:
 * - BaseService Pattern: Lifecycle management (IDLE → INITIALIZING → INITIALIZED/FAILED)
 * - Dependency Graph: Topological sort for initialization ordering
 * - Type-Safe Registry: Symbol-based service keys with phantom types
 * - Parallel Initialization: Concurrent service startup (~700ms savings)
 *
 * KEY FEATURES:
 * - Topological sort with DFS for automatic initialization ordering
 * - Circular dependency detection (throws on cycle)
 * - Level-based grouping (services at same level can init in parallel)
 * - Priority sorting within levels (lower number = higher priority)
 * - Dependent service lookup (impact analysis, shutdown ordering)
 *
 * RELATED FILES:
 * - src/lib/services/core/BaseService.ts
 * - src/lib/services/core/ServiceManager.ts
 * - src/lib/services/core/registry.ts
 */

// services/core/dependency-graph.ts
/**
 * Dependency Graph with Topological Sort
 *
 * Calculates correct service initialization order, detects circular dependencies,
 * and groups services into parallel initialization levels.
 *
 * @example
 * ```typescript
 * // Add services to graph
 * graph.addNode({ key: SERVICE_KEYS.AUTH, dependencies: [], critical: true, lazy: false, priority: 0 });
 * graph.addNode({ key: SERVICE_KEYS.WEBSOCKET, dependencies: [SERVICE_KEYS.AUTH], critical: true, lazy: false, priority: 0 });
 *
 * // Get initialization order
 * const levels = graph.getInitializationLevels();
 * // Result: [[AUTH], [WEBSOCKET], ...]
 * // Services at the same level can initialize in parallel!
 * ```
 */

import type { ServiceDefinition } from './registry';

/**
 * Node in the dependency graph with computed initialization level
 */
export interface ServiceNode {
	/** Unique symbol key for this service */
	key: symbol;

	/** Service definition metadata */
	definition: ServiceDefinition;

	/** Computed initialization level (0 = no deps, 1+ = depends on lower levels) */
	level: number;
}

/**
 * Dependency graph with topological sort for service initialization ordering
 *
 * Features:
 * - Circular dependency detection (throws Error if cycle found)
 * - Parallel initialization levels (services at same level can init in parallel)
 * - Priority sorting within levels (lower number = higher priority)
 * - Dependent service lookup (what depends on this service?)
 */
export class DependencyGraph {
	private nodes = new Map<symbol, ServiceNode>();

	/**
	 * Add a service node to the graph
	 *
	 * @param definition - Service definition with dependencies and metadata
	 *
	 * @example
	 * ```typescript
	 * graph.addNode({
	 *   key: SERVICE_KEYS.WEBSOCKET,
	 *   dependencies: [SERVICE_KEYS.AUTH],
	 *   critical: true,
	 *   lazy: false,
	 *   priority: 0
	 * });
	 * ```
	 */
	addNode(definition: ServiceDefinition): void {
		this.nodes.set(definition.key, {
			key: definition.key,
			definition,
			level: -1 // Will be computed by getInitializationLevels()
		});
	}

	/**
	 * Compute initialization levels via topological sort
	 *
	 * Returns array of arrays where:
	 * - Each inner array contains services that can initialize in parallel
	 * - Outer array index = initialization level (0 first, then 1, etc.)
	 * - Services within a level are sorted by priority (lower = higher priority)
	 *
	 * Algorithm:
	 * 1. Use DFS to detect circular dependencies (throws if cycle found)
	 * 2. Compute level for each service (max(dependency levels) + 1)
	 * 3. Group services by level
	 * 4. Sort within each level by priority
	 *
	 * @returns Array of symbol arrays grouped by initialization level
	 * @throws {Error} If circular dependency detected
	 * @throws {Error} If service referenced in dependencies but not found in graph
	 *
	 * @example
	 * ```typescript
	 * // Given dependencies:
	 * // AUTH: no deps
	 * // WEBSOCKET: depends on AUTH
	 * // ALERTS: depends on WEBSOCKET
	 *
	 * const levels = graph.getInitializationLevels();
	 * // Result:
	 * // [
	 * //   [SERVICE_KEYS.AUTH],           // Level 0: can start immediately
	 * //   [SERVICE_KEYS.WEBSOCKET],      // Level 1: depends on level 0
	 * //   [SERVICE_KEYS.ALERTS]          // Level 2: depends on level 1
	 * // ]
	 * ```
	 */
	getInitializationLevels(): symbol[][] {
		const levels: symbol[][] = [];
		const visited = new Set<symbol>();
		const visiting = new Set<symbol>(); // Track nodes currently being visited (for cycle detection)
		const levelMap = new Map<symbol, number>();

		/**
		 * Recursive DFS to compute levels and detect cycles
		 *
		 * @param key - Service key to visit
		 * @returns Computed level for this service
		 * @throws {Error} If circular dependency detected
		 */
		const visit = (key: symbol): number => {
			// Already computed this node's level
			if (visited.has(key)) {
				return levelMap.get(key)!;
			}

			// Circular dependency detected!
			if (visiting.has(key)) {
				throw new Error(`Circular dependency detected at: ${String(key)}`);
			}

			// Mark as currently visiting (for cycle detection)
			visiting.add(key);

			// Get node from graph
			const node = this.nodes.get(key);
			if (!node) {
				throw new Error(`Service not found in graph: ${String(key)}`);
			}

			// Visit all dependencies first to compute their levels
			let maxDepLevel = -1;
			for (const depKey of node.definition.dependencies) {
				const depLevel = visit(depKey);
				maxDepLevel = Math.max(maxDepLevel, depLevel);
			}

			// This service's level is one more than its highest dependency
			// If no dependencies, level is 0
			const level = maxDepLevel + 1;
			levelMap.set(key, level);
			node.level = level;

			// Done visiting this node
			visiting.delete(key);
			visited.add(key);

			return level;
		};

		// Visit all nodes to compute levels
		for (const key of this.nodes.keys()) {
			visit(key);
		}

		// Group services by level
		for (const [key, level] of levelMap.entries()) {
			// Ensure levels array has enough slots
			while (levels.length <= level) {
				levels.push([]);
			}
			levels[level].push(key);
		}

		// Sort within each level by priority (lower number = higher priority)
		for (const level of levels) {
			level.sort((a, b) => {
				const nodeA = this.nodes.get(a)!;
				const nodeB = this.nodes.get(b)!;
				return nodeA.definition.priority - nodeB.definition.priority;
			});
		}

		return levels;
	}

	/**
	 * Get all services that depend on a given service
	 *
	 * Useful for:
	 * - Impact analysis (what breaks if this service fails?)
	 * - Shutdown ordering (shutdown dependents first)
	 * - Dependency visualization
	 *
	 * @param key - Service key to check dependents for
	 * @returns Array of service keys that depend on this service
	 *
	 * @example
	 * ```typescript
	 * const dependents = graph.getDependents(SERVICE_KEYS.AUTH);
	 * // Returns: [SERVICE_KEYS.WEBSOCKET, SERVICE_KEYS.ALERTS, ...]
	 * // All services that have AUTH in their dependencies array
	 * ```
	 */
	getDependents(key: symbol): symbol[] {
		const dependents: symbol[] = [];
		for (const [nodeKey, node] of this.nodes.entries()) {
			if (node.definition.dependencies.includes(key)) {
				dependents.push(nodeKey);
			}
		}
		return dependents;
	}

	/**
	 * Validate graph for circular dependencies
	 *
	 * Safe alternative to getInitializationLevels() that returns validation result
	 * instead of throwing. Useful for pre-flight checks.
	 *
	 * @returns Validation result with success flag and error messages
	 *
	 * @example
	 * ```typescript
	 * const result = graph.validate();
	 * if (!result.valid) {
	 *   console.error('Dependency graph errors:', result.errors);
	 *   // Handle errors gracefully
	 * }
	 * ```
	 */
	validate(): { valid: boolean; errors: string[] } {
		const errors: string[] = [];

		try {
			this.getInitializationLevels();
		} catch (error) {
			errors.push(error instanceof Error ? error.message : String(error));
		}

		return {
			valid: errors.length === 0,
			errors
		};
	}

	/**
	 * Get a specific node by key
	 *
	 * @param key - Service key
	 * @returns ServiceNode or undefined if not found
	 */
	getNode(key: symbol): ServiceNode | undefined {
		return this.nodes.get(key);
	}

	/**
	 * Get all nodes in the graph
	 *
	 * @returns Array of all service nodes
	 */
	getAllNodes(): ServiceNode[] {
		return Array.from(this.nodes.values());
	}

	/**
	 * Get number of nodes in graph
	 *
	 * @returns Total number of services in graph
	 */
	size(): number {
		return this.nodes.size;
	}

	/**
	 * Clear all nodes from graph
	 *
	 * Mainly for testing - allows resetting graph state
	 */
	clear(): void {
		this.nodes.clear();
	}
}

/**
 * Global dependency graph instance
 *
 * Use this throughout the application for service dependency management.
 * Services should be registered here with their dependencies before initialization.
 */
export const dependencyGraph = new DependencyGraph();
